import { captureCustomizerMessage, incrementCustomizerMetric } from "./sentry";
import { reportingFetch } from "./httpReporting";
import { safeGetJson, safeRemoveItem, safeSetJson } from "./safeStorage";

const CUSTOMIZER_PATH = "/pages/customizer";
const LAST_TARGET_URL_KEY = "customizer:lastTargetUrl";
// sessionStorage only lives in one tab. The shared copy also covers a new tab or
// another browser window, but only briefly so an old product is not reopened.
const SHARED_LAST_TARGET_URL_KEY = "customizer:lastTargetUrlShared";
const SHARED_LAST_TARGET_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const RECOVERY_ATTEMPT_KEY = "customizer:urlRecoveryAttempt";
const IS_SHOPIFY_BUILD = process.env.VUE_APP_BUILD_SHOPIFY === "true";
const memorySessionStorage = new Map();

function getSessionItem(key, defaultValue = "") {
    try {
        const value = window.sessionStorage?.getItem(key);
        return value === null || value === undefined ? defaultValue : value;
    } catch (error) {
        // sentry-ignore: sessionStorage unavailable; in-memory fallback
        void error;
        return memorySessionStorage.get(key) || defaultValue;
    }
}

function setSessionItem(key, value) {
    memorySessionStorage.set(key, String(value));

    try {
        window.sessionStorage?.setItem(key, String(value));
    } catch (error) {
        // sentry-ignore: sessionStorage unavailable; in-memory fallback
        void error;
    }
}

function removeSessionItem(key) {
    memorySessionStorage.delete(key);

    try {
        window.sessionStorage?.removeItem(key);
    } catch (error) {
        // sentry-ignore: sessionStorage unavailable; in-memory fallback
        void error;
    }
}

function getStepFromHash(hash) {
    const match = (hash || "").match(/^#\/([^/?#]+)\/(step[123])(?:[?#].*)?$/);
    if (!match) return null;

    return {
        variant: match[1],
        step: match[2],
    };
}

function getStepFromPath(pathname) {
    const match = (pathname || "").match(/^\/([^/?#]+)\/(step[123])\/?$/);
    if (!match) return null;

    return {
        variant: match[1],
        step: match[2],
    };
}

function getStepFromSearch(url) {
    const variant = url.searchParams.get("variant");
    const step = url.searchParams.get("step") || "step1";
    if (!variant || !/^step[123]$/.test(step)) return null;

    return { variant, step };
}

function isStandaloneCustomizerUrl(url) {
    if (IS_SHOPIFY_BUILD) return false;

    return (
        url.pathname === "/" ||
        url.pathname === "" ||
        url.pathname.includes(CUSTOMIZER_PATH) ||
        Boolean(getStepFromPath(url.pathname)) ||
        Boolean(getStepFromHash(url.hash)) ||
        Boolean(getStepFromSearch(url))
    );
}

function isCustomizerUrl(url) {
    return url.pathname.includes(CUSTOMIZER_PATH) || isStandaloneCustomizerUrl(url);
}

function hasValidCustomizerRoute(url) {
    if (!IS_SHOPIFY_BUILD && getStepFromPath(url.pathname)) {
        return true;
    }

    return Boolean(getStepFromHash(url.hash));
}

function applyStandaloneRoute(url, stepData) {
    url.pathname = `/${stepData.variant}/${stepData.step}`;
    url.searchParams.delete("variant");
    url.searchParams.delete("step");
    url.hash = "";
    return url;
}

function applyShopifyRoute(url, stepData) {
    if (url.pathname === CUSTOMIZER_PATH) {
        url.pathname = `${CUSTOMIZER_PATH}/`;
    }

    if (!url.searchParams.get("variant")) {
        url.searchParams.set("variant", stepData.variant);
    }

    if (!url.searchParams.get("step")) {
        url.searchParams.set("step", stepData.step);
    }

    url.hash = `#/${stepData.variant}/${stepData.step}`;
    return url;
}

function normalizeCustomizerUrl(inputUrl) {
    const fixedHashUrl = String(inputUrl).replace(
        `${CUSTOMIZER_PATH}#/`,
        `${CUSTOMIZER_PATH}/#/`
    );
    const url = new URL(fixedHashUrl, window.location.origin);
    if (!isCustomizerUrl(url)) return url;

    const stepData =
        getStepFromHash(url.hash) ||
        getStepFromSearch(url) ||
        getStepFromPath(url.pathname);

    if (!stepData) {
        if (url.pathname === CUSTOMIZER_PATH) {
            url.pathname = `${CUSTOMIZER_PATH}/`;
        }
        return url;
    }

    if (!IS_SHOPIFY_BUILD) {
        return applyStandaloneRoute(url, stepData);
    }

    return applyShopifyRoute(url, stepData);
}

function getAttemptKey() {
    return `${RECOVERY_ATTEMPT_KEY}:${window.location.pathname}${window.location.search}`;
}

function hasAttemptedRecovery() {
    return getSessionItem(getAttemptKey(), "") === "1";
}

function markRecoveryAttempted() {
    setSessionItem(getAttemptKey(), "1");
}

function getStepDataFromUrl(url) {
    if (!url) return null;

    return (
        getStepFromHash(url.hash) ||
        getStepFromSearch(url) ||
        getStepFromPath(url.pathname)
    );
}

function getRouteCandidate(url) {
    const validHash = getStepFromHash(url.hash);
    if (validHash) return { source: "hash", ...validHash };

    const validSearch = getStepFromSearch(url);
    if (validSearch) return { source: "query", ...validSearch };

    const validPath = getStepFromPath(url.pathname);
    if (validPath) return { source: "path", ...validPath };

    const hashMatch = (url.hash || "").match(
        /^#\/([^/?#]+)(?:\/([^/?#]+))?/
    );
    if (hashMatch) {
        return {
            source: "hash",
            variant: hashMatch[1],
            step: hashMatch[2] || "",
        };
    }

    const searchVariant = url.searchParams.get("variant");
    if (searchVariant) {
        return {
            source: "query",
            variant: searchVariant,
            step: url.searchParams.get("step") || "step1",
        };
    }

    const pathMatch = (url.pathname || "").match(
        /^\/([^/?#]+)\/(step[^/?#]*)\/?$/
    );
    if (pathMatch) {
        return {
            source: "path",
            variant: pathMatch[1],
            step: pathMatch[2],
        };
    }

    return null;
}

function getRecoveryReason(url) {
    const candidate = getRouteCandidate(url);
    if (!candidate?.variant) return "missing_variant";
    if (!/^step[123]$/.test(candidate.step || "")) return "invalid_step";
    return "no_candidate";
}

function reportRecovery(
    message,
    nextUrl = null,
    level = "error",
    diagnostics = {}
) {
    const currentUrl = new URL(window.location.href);
    const stepData = getStepDataFromUrl(nextUrl) || getStepDataFromUrl(currentUrl);
    const routeSource = diagnostics.routeSource ||
        getRouteCandidate(currentUrl)?.source ||
        "none";
    const recoveryReason = diagnostics.recoveryReason ||
        (nextUrl ? "recovered" : getRecoveryReason(currentUrl));
    const tags = {
        host: window.location.host,
        path: window.location.pathname,
        variant: stepData?.variant || "unknown",
        step: stepData?.step || "unknown",
        hasHash: Boolean(window.location.hash),
        recovered: Boolean(nextUrl),
        route_source: routeSource,
        recovery_reason: recoveryReason,
    };
    const context = {
        urlPath: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        nextPath: nextUrl?.pathname,
        nextSearch: nextUrl?.search,
        nextHash: nextUrl?.hash,
        referrerHost: document.referrer
            ? new URL(document.referrer).host
            : "",
    };

    if (message === "customizer_url_recovered") {
        incrementCustomizerMetric("customizer.url_recovered", {
            host: tags.host,
            step: tags.step,
            hasHash: tags.hasHash,
            route_source: tags.route_source,
            recovery_reason: tags.recovery_reason,
        });
        return;
    }

    captureCustomizerMessage(message, {
        level,
        tags,
        context,
    });
}

function replaceUrl(nextUrl, reason, routeSource) {
    markRecoveryAttempted();
    window.history.replaceState(window.history.state, "", nextUrl.toString());
    reportRecovery(reason, nextUrl, "info", {
        routeSource,
        recoveryReason: "recovered",
    });
}

function normalizeCurrentCustomizerUrl() {
    if (!isCustomizerUrl(window.location)) return false;

    const currentUrl = new URL(window.location.href);
    const normalizedUrl = normalizeCustomizerUrl(window.location.href);

    if (normalizedUrl.toString() !== window.location.href) {
        replaceUrl(
            normalizedUrl,
            "customizer_url_recovered",
            getRouteCandidate(currentUrl)?.source || "none"
        );
        return true;
    }

    return false;
}

function getSharedTargetUrl() {
    const shared = safeGetJson(SHARED_LAST_TARGET_URL_KEY, null);
    if (!shared?.url || Date.now() - Number(shared.at) > SHARED_LAST_TARGET_MAX_AGE_MS) {
        return "";
    }
    return shared.url;
}

function recoverFromStoredTarget() {
    const storedTarget = getSessionItem(LAST_TARGET_URL_KEY, "") || getSharedTargetUrl();
    if (!storedTarget) return false;

    const targetUrl = normalizeCustomizerUrl(storedTarget);
    if (!isCustomizerUrl(targetUrl) || !hasValidCustomizerRoute(targetUrl)) {
        return false;
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.pathname = targetUrl.pathname;
    nextUrl.search = targetUrl.search;
    nextUrl.hash = targetUrl.hash;
    replaceUrl(nextUrl, "customizer_url_recovered", "stored_target");
    return true;
}

async function recoverFromReferrer() {
    if (!document.referrer) return false;

    const referrerUrl = new URL(document.referrer);
    if (referrerUrl.origin !== window.location.origin) return false;
    if (!referrerUrl.pathname.includes("/products/")) return false;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 1200);

    try {
        const response = await reportingFetch(referrerUrl.toString(), {
            credentials: "same-origin",
            signal: controller.signal,
        }, {
            // sentry-ignore: best-effort recovery with a 1.2s timeout; a miss falls back to the normal flow
            sentryReport: false,
        });
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const link = doc.querySelector('a[href*="/pages/customizer"]');
        const href = link?.getAttribute("href");

        if (!href) return false;

        const targetUrl = normalizeCustomizerUrl(href);
        if (!hasValidCustomizerRoute(targetUrl)) return false;

        const nextUrl = new URL(window.location.href);
        nextUrl.pathname = targetUrl.pathname;
        nextUrl.search = targetUrl.search;
        nextUrl.hash = targetUrl.hash;
        replaceUrl(nextUrl, "customizer_url_recovered", "product_referrer");
        return true;
    } catch (error) {
        // sentry-ignore: best-effort URL recovery; normal flow continues
        void error;
        return false;
    } finally {
        window.clearTimeout(timeoutId);
    }
}

function reportUnrecoverableIfNeeded() {
    if (!isCustomizerUrl(window.location)) return;
    if (hasValidCustomizerRoute(window.location)) return;

    reportRecovery("customizer_url_unrecoverable", null, "error");
}

export function rememberCustomizerTargetUrl(inputHref) {
    if (!inputHref) return inputHref;

    try {
        const targetUrl = normalizeCustomizerUrl(inputHref);
        if (!isCustomizerUrl(targetUrl)) return inputHref;

        setSessionItem(LAST_TARGET_URL_KEY, targetUrl.toString());
        safeSetJson(SHARED_LAST_TARGET_URL_KEY, { url: targetUrl.toString(), at: Date.now() });
        return targetUrl.toString();
    } catch (error) {
        // sentry-ignore: invalid href is returned unchanged
        void error;
        return inputHref;
    }
}

export async function recoverCustomizerUrl() {
    if (!isCustomizerUrl(window.location)) return;

    if (normalizeCurrentCustomizerUrl()) return;
    if (hasValidCustomizerRoute(window.location)) return;
    if (hasAttemptedRecovery()) {
        reportUnrecoverableIfNeeded();
        return;
    }

    markRecoveryAttempted();

    if (recoverFromStoredTarget()) return;
    if (await recoverFromReferrer()) return;

    removeSessionItem(LAST_TARGET_URL_KEY);
    safeRemoveItem(SHARED_LAST_TARGET_URL_KEY);
    reportUnrecoverableIfNeeded();
}
